import React from "react";
import { formatINR, formatNum } from "@/lib/api";
import { Separator } from "@/components/ui/separator";

/**
 * Customer-facing price breakdown (storefront Product Detail page).
 *
 * Diamond & colour-stone lines always show a single blended "Rate/ct" that already has the
 * internal wholesale discount/markup baked in (`effective_rate_per_carat` / `effective_value`,
 * computed server-side in `catalog_service.price_product`). The raw base rate and the
 * discount/markup % itself are intentionally never shown here — that stays admin-only in the
 * ERP order view. Subtotal/GST/Total use the same post-discount figures so every line on screen
 * adds up to the Total exactly as printed.
 */
export const PriceBreakdown = ({ pricing }) => {
  if (!pricing) return null;
  const g = pricing.gold || {};
  const rows = [];

  if (g.metal_value > 0) {
    rows.push({
      label: `Gold metal (${formatNum(g.weight_g)}g × ${formatINR(g.rate_per_gram_24k)} × ${formatNum(g.purity_factor, 3)})`,
      value: g.metal_value,
    });
    if (g.wastage_value > 0) rows.push({ label: `Wastage (${formatNum(g.wastage_pct)}%)`, value: g.wastage_value });
    if (g.making_value > 0) rows.push({
      label: g.making_type === "percent" ? `Making charges (${formatNum(g.making_input)}%)` : "Making charges",
      value: g.making_value,
    });
  }
  (pricing.diamonds || []).forEach((d) => {
    const rate = d.effective_rate_per_carat ?? d.rate_per_carat;
    const value = d.effective_value ?? d.value;
    rows.push({
      label: `Diamond ${d.quality}${d.shape ? " · " + d.shape : ""} (${formatNum(d.carat)}ct total${d.count > 1 ? `, ${d.count} pcs` : ""} @ ${formatINR(rate)}/ct)`,
      value,
    });
  });
  (pricing.colour_stones || []).forEach((s) => {
    const rate = s.effective_rate_per_carat ?? s.rate_per_carat;
    const value = s.effective_value ?? s.value;
    rows.push({
      label: `${s.quality} (${formatNum(s.carat)}ct total${s.count > 1 ? `, ${s.count} pcs` : ""} @ ${formatINR(rate)}/ct)`,
      value,
    });
  });
  // Combined into a single line (rather than two separate rows) since they're both small,
  // easy-to-miss fixed charges — but still MUST be shown, since they're already baked into the
  // subtotal server-side and omitting them entirely is what caused line items to look like they
  // didn't add up to the Total.
  const certHallmarkTotal = (pricing.certification_charges || 0) + (pricing.hallmarking_charges || 0);
  if (certHallmarkTotal > 0) rows.push({ label: "Hallmarking & certification charges", value: certHallmarkTotal });

  const subtotal = pricing.net_subtotal ?? pricing.subtotal;
  const gstAmount = pricing.net_gst_amount ?? pricing.gst_amount;
  const total = pricing.net_total ?? pricing.total;

  return (
    <div data-testid="pdp-price-breakdown" className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-body">
        Price Breakdown
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={`${r.label}-${i}`} className="flex items-start justify-between gap-4 text-sm">
            <span className="text-muted-foreground min-w-0 flex-1 break-words">{r.label}</span>
            <span className="font-mono text-foreground whitespace-nowrap shrink-0">{formatINR(r.value)}</span>
          </div>
        ))}
      </div>
      <Separator className="my-3" />
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Subtotal</span>
        <span className="font-mono">{formatINR(subtotal)}</span>
      </div>
      <div className="flex items-center justify-between text-sm mt-1">
        <span className="text-muted-foreground">GST ({formatNum(pricing.gst_pct)}%)</span>
        <span className="font-mono">{formatINR(gstAmount)}</span>
      </div>
      <Separator className="my-3" />
      <div className="flex items-center justify-between">
        <span className="font-display text-lg">Total</span>
        <span data-testid="pdp-total-price" className="font-mono font-bold text-xl text-[hsl(var(--primary))]">{formatINR(total)}</span>
      </div>
    </div>
  );
};
